import { Host, forceUpdate, h } from "@stencil/core";
import { getWidgetDropInfo, handleWidgetDrag, removeDroppableAreaStyles } from "./utils";
import { getLeafInfo } from "../../utils";
import { isRTL, tokenMap } from "../../../../common/utils";
import { DEFAULT_TAB_LIST_POSITION, isBlockDirection, isStartDirection } from "../../../tab/utils";
import { FLEXIBLE_LAYOUT_PARTS_DICTIONARY, LAYOUT_SPLITTER_PARTS_DICTIONARY, TAB_EXPORT_PARTS, TAB_PARTS_DICTIONARY } from "../../../../common/reserved-names";
const LEAF_SELECTOR = (id) => `[id="${id}"]`;
// Keys
const ESCAPE_KEY = "Escape";
// const KEY_B = "KeyB";
export class ChFlexibleLayout {
    constructor() {
        this.dragBarDisabled = false;
        this.closeButton = false;
        this.contain = "none";
        this.dragOutside = false;
        this.itemsInfo = undefined;
        this.layoutSplitterParts = undefined;
        this.model = undefined;
        this.overflow = "visible";
        this.sortable = false;
    }
    #exportParts;
    #layoutSplitterExportParts;
    #leafs = [];
    #draggableViews;
    #dragInfo;
    #viewsOutOfDroppableZoneController; // Allocated at runtime to reduce memory usage
    // Refs
    #draggedViewRef;
    #droppableAreaRef;
    #layoutSplitterRef;
    el;
    /**
     * Fired when a item of a view request to be closed.
     */
    viewItemClose;
    /**
     * Fired when the selected item change.
     */
    selectedViewItemChange;
    /**
     * Fired when a widget is dragged and dropped into a view.
     */
    viewItemReorder;
    /**
     *
     */
    async addSiblingView(parentGroup, siblingItem, placedInTheSibling, viewInfo, takeHalfTheSpaceOfTheSiblingItem) {
        const result = await this.#layoutSplitterRef.addSiblingLeaf(parentGroup, siblingItem, placedInTheSibling, viewInfo, takeHalfTheSpaceOfTheSiblingItem);
        if (result.success) {
            // Queue re-renders
            forceUpdate(this);
        }
        return result.success;
    }
    /**
     * Schedules a new render for a leaf even if no state changed.
     */
    async refreshLeaf(leafId) {
        const leafRef = this.el.shadowRoot.querySelector(LEAF_SELECTOR(leafId));
        if (!leafRef) {
            return;
        }
        forceUpdate(leafRef);
    }
    /**
     * Removes the view that is identified by the given ID.
     * The layout is rearranged depending on the state of the removed view.
     */
    async removeView(itemId) {
        const result = await this.#layoutSplitterRef.removeItem(itemId);
        if (result.success) {
            // Queue re-renders
            forceUpdate(this);
        }
        return {
            success: result.success,
            reconnectedSubtree: result.reconnectedSubtree
        };
    }
    /**
     * Given the view ID and the item id, remove the page of the item from the view.
     */
    async removeItemPageInView(viewId, itemId, forceRerender = true) {
        const viewInfo = this.#getLeafInfo(viewId);
        if (!viewInfo) {
            return;
        }
        const viewRef = this.el.shadowRoot.querySelector(LEAF_SELECTOR(viewInfo.id));
        await viewRef.removePage(itemId, forceRerender);
    }
    handleItemClose(event) {
        const viewRef = event.composedPath()[0];
        if (viewRef.tagName.toLowerCase() !== "ch-tab-render" ||
            viewRef.getRootNode() !== this.el.shadowRoot) {
            return;
        }
        event.stopPropagation();
        // Add the view id to properly update the render
        const eventDetail = {
            ...event.detail,
            viewId: viewRef.id
        };
        const eventInfo = this.viewItemClose.emit(eventDetail);
        // Prevent the tab closing
        if (eventInfo.defaultPrevented) {
            event.preventDefault();
        }
    }
    #getLeafInfo = (leafId) => getLeafInfo(this.itemsInfo, leafId);
    #getAllLeafs = () => {
        const views = [];
        this.itemsInfo.forEach(item => {
            const itemView = item.leafInfo;
            if (itemView != null) {
                views.push(itemView);
            }
        });
        return views;
    };
    // @Listen("keydown", { target: "document" })
    // handleKeyDownEvent(event: KeyboardEvent) {
    // if (
    //   !mouseEventModifierKey(event) ||
    //   event.code !== KEY_B ||
    //   this.layout.inlineStart == null
    // ) {
    //   return;
    // }
    // event.stopPropagation();
    // event.preventDefault();
    // this.layout.inlineStart.expanded = !(
    //   this.layout.inlineStart.expanded ?? true
    // );
    //   forceUpdate(this);
    // }
    // private handleMainGroupExpand = () => {
    //   // if (this.layout.inlineStart) {
    //   //   this.layout.inlineStart.expanded = false;
    //   // }
    //   // if (this.layout.inlineEnd) {
    //   //   this.layout.inlineEnd.expanded = false;
    //   // }
    //   // if (this.layout.blockEnd) {
    //   //   this.layout.blockEnd.expanded = false;
    //   // }
    //   forceUpdate(this);
    // };
    #handleItemChange = (viewId) => (event) => {
        // Check if the selected item change event comes from a tab of the
        // shadowroot, instead of a tab of the light DOM (case where a view has a
        // "nested" ch-tab-render control)
        if (event.target.getRootNode() !== this.el.shadowRoot) {
            return;
        }
        event.stopPropagation();
        // Add the view id to properly update the render
        const eventInfo = {
            ...event.detail,
            viewId: viewId
        };
        this.selectedViewItemChange.emit(eventInfo);
    };
    #handleDragStart = (viewId) => async (event) => {
        event.stopPropagation();
        // We MUST store the reference before the Promise.allSettle, otherwise
        // the event target will be the flexible-layout control
        this.#draggedViewRef = event.target;
        const views = [...this.el.shadowRoot.querySelectorAll("ch-tab-render")];
        this.#dragInfo = {
            index: event.detail,
            viewId: viewId
        };
        // Get all draggable views
        const draggableViewsResult = await Promise.allSettled(views.map(view => view.getDraggableViews()));
        // Allocate memory
        this.#draggableViews = [];
        // Add handlers to manage droppable areas
        draggableViewsResult.forEach((draggableViewResult, index) => {
            if (draggableViewResult.status === "fulfilled") {
                const draggableView = draggableViewResult.value;
                const abortController = new AbortController(); // Necessary to remove the event listener
                const extendedDraggableView = {
                    ...draggableView,
                    viewId: views[index].id, // All views have an id in the DOM
                    abortController: abortController
                };
                this.#draggableViews.push(extendedDraggableView);
                const RTL = isRTL();
                draggableView.mainView.addEventListener("mousemove", handleWidgetDrag(extendedDraggableView, this.#droppableAreaRef, RTL), { capture: true, passive: true, signal: abortController.signal });
                // Remove pointer events to not interfere on the mousemove event
                extendedDraggableView.tabListView.style.pointerEvents = "none";
                extendedDraggableView.pageView.style.pointerEvents = "none";
            }
        });
        document.addEventListener("mouseup", this.#handleWidgetDragEnd, {
            passive: true
        });
        document.addEventListener("keydown", this.#handleWidgetDragEndKeydown, {
            passive: true
        });
        // Removes view when they are out of a droppable area
        this.#viewsOutOfDroppableZoneController = new AbortController();
        document.addEventListener("mousemove", () => removeDroppableAreaStyles(this.#droppableAreaRef), {
            passive: true,
            signal: this.#viewsOutOfDroppableZoneController.signal
        });
        // Show droppable area
        this.#droppableAreaRef.showPopover(); // Layer 1
        // After that, promote the drag preview to the second layer
        this.#draggedViewRef.promoteDragPreviewToTopLayer(); // Layer 2
        // Disable drag bars in layout-splitter to improve the drag experience
        this.dragBarDisabled = true;
    };
    #handleWidgetDragEndKeydown = (event) => {
        if (event.code !== ESCAPE_KEY) {
            return;
        }
        event.preventDefault();
        // Cancels the drop by removing the drop info
        removeDroppableAreaStyles(this.#droppableAreaRef);
        this.#handleWidgetDragEnd();
        this.#draggedViewRef.endDragPreview();
    };
    #handleWidgetDragEnd = () => {
        // Remove mousemove handlers
        this.#draggableViews.forEach(draggableView => {
            draggableView.abortController.abort();
            // Reset pointer events
            draggableView.tabListView.style.pointerEvents = null;
            draggableView.pageView.style.pointerEvents = null;
        });
        // Remove mouseup and keydown handlers
        document.removeEventListener("mouseup", this.#handleWidgetDragEnd);
        document.removeEventListener("keydown", this.#handleWidgetDragEndKeydown);
        this.#viewsOutOfDroppableZoneController.abort();
        // Check if must update the view due to a drop
        const dropInfo = getWidgetDropInfo();
        if (dropInfo) {
            this.viewItemReorder.emit({ ...this.#dragInfo, ...dropInfo });
        }
        // Hide droppable area
        this.#droppableAreaRef.hidePopover();
        removeDroppableAreaStyles(this.#droppableAreaRef);
        // Free the memory
        this.#draggableViews = undefined;
        this.#dragInfo = undefined;
        // Re-enable drag bars
        this.dragBarDisabled = false;
    };
    #computePartsToExport = () => {
        const exportPartsSet = new Set([
            ...Object.values(TAB_PARTS_DICTIONARY)
        ]);
        exportPartsSet.add(FLEXIBLE_LAYOUT_PARTS_DICTIONARY.DROPPABLE_AREA);
        exportPartsSet.add(FLEXIBLE_LAYOUT_PARTS_DICTIONARY.LEAF);
        exportPartsSet.add(LAYOUT_SPLITTER_PARTS_DICTIONARY.BAR);
        // TODO: Test items that have a part with spaces
        this.layoutSplitterParts.forEach(part => exportPartsSet.add(part));
        // TODO: Revisit this algorithm to simplify definition of exportparts
        this.#leafs.forEach(leaf => {
            if (leaf.type === "tabbed") {
                exportPartsSet.add(leaf.id);
                exportPartsSet.add(leaf.tabListPosition ?? DEFAULT_TAB_LIST_POSITION);
                leaf.widgets.forEach(({ id }) => exportPartsSet.add(id));
            }
        });
        this.#exportParts = [...exportPartsSet.keys()].join(",");
        this.#layoutSplitterExportParts = [
            ...this.layoutSplitterParts.keys(),
            LAYOUT_SPLITTER_PARTS_DICTIONARY.BAR
        ].join(",");
    };
    #renderTab = (viewInfo) => {
        const dragOutsideEnabled = viewInfo.dragOutside ?? this.dragOutside;
        const sortableEnabled = viewInfo.sortable ?? this.sortable;
        const tabListPosition = viewInfo.tabListPosition ?? DEFAULT_TAB_LIST_POSITION;
        const blockDirection = isBlockDirection(tabListPosition);
        const startDirection = isStartDirection(tabListPosition);
        return (h("ch-tab-render", { id: viewInfo.id, key: viewInfo.id, slot: viewInfo.id, contain: this.contain,
            // TODO: Add hostParts property in the ch-tab-render
            part: tokenMap({
                [viewInfo.id]: true,
                [FLEXIBLE_LAYOUT_PARTS_DICTIONARY.LEAF]: true,
                [tabListPosition]: true,
                [TAB_PARTS_DICTIONARY.BLOCK]: blockDirection,
                [TAB_PARTS_DICTIONARY.INLINE]: !blockDirection,
                [TAB_PARTS_DICTIONARY.START]: startDirection,
                [TAB_PARTS_DICTIONARY.END]: !startDirection
            }),
            // TODO: Find a better way to avoid this mapping on every render
            exportparts: `${TAB_EXPORT_PARTS},${tabListPosition},${viewInfo.widgets
                .map(({ id }) => id)
                .join(",")}`, closeButton: viewInfo.closeButton ?? this.closeButton, disabled: viewInfo.disabled, dragOutside: dragOutsideEnabled, model: viewInfo.widgets, overflow: this.overflow, selectedId: viewInfo.selectedWidgetId, showCaptions: viewInfo.showCaptions, sortable: sortableEnabled, tabButtonHidden: viewInfo.tabButtonHidden, tabListPosition: tabListPosition,
            // onExpandMainGroup={tabType === "main" ? this.handleMainGroupExpand : null}
            onItemDragStart: dragOutsideEnabled && sortableEnabled
                ? this.#handleDragStart(viewInfo.id)
                : undefined, onSelectedItemChange: this.#handleItemChange(viewInfo.id) }, viewInfo.widgets.map(widget => widget.wasRendered && h("slot", { name: widget.id, slot: widget.id }))));
    };
    #renderView = (leaf) => leaf.type === "single-content" ? (h("slot", { key: leaf.id, slot: leaf.id, name: leaf.id })) : (this.#renderTab(leaf));
    componentWillRender() {
        this.#leafs = this.#getAllLeafs();
        // TODO: Find a better life cycle to run this?
        this.#computePartsToExport();
    }
    render() {
        const layoutModel = this.model;
        if (layoutModel == null) {
            return "";
        }
        return (h(Host, { exportparts: this.#exportParts }, h("ch-layout-splitter", { dragBarDisabled: this.dragBarDisabled, model: layoutModel, exportparts: this.#layoutSplitterExportParts, ref: el => (this.#layoutSplitterRef = el) }, this.#leafs.map(this.#renderView)), h("div", { "aria-hidden": "true", class: "droppable-area", part: FLEXIBLE_LAYOUT_PARTS_DICTIONARY.DROPPABLE_AREA, popover: "manual", ref: el => (this.#droppableAreaRef = el) })));
    }
    static get is() { return "ch-flexible-layout"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["flexible-layout.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["flexible-layout.css"]
        };
    }
    static get properties() {
        return {
            "closeButton": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to display a close button for the `\"tabbed\"` type leafs."
                },
                "attribute": "close-button",
                "reflect": false,
                "defaultValue": "false"
            },
            "contain": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "CssContainProperty",
                    "resolved": "\"content\" | \"inline-size\" | \"layout\" | \"none\" | \"paint\" | \"size\" | \"strict\" | \"style\"",
                    "references": {
                        "CssContainProperty": {
                            "location": "import",
                            "path": "../../../../common/types",
                            "id": "src/common/types.ts::CssContainProperty"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Same as the contain CSS property. This property indicates that an widget\nand its contents are, as much as possible, independent from the rest of the\ndocument tree. Containment enables isolating a subsection of the DOM,\nproviding performance benefits by limiting calculations of layout, style,\npaint, size, or any combination to a DOM subtree rather than the entire\npage.\nContainment can also be used to scope CSS counters and quotes."
                },
                "attribute": "contain",
                "reflect": false,
                "defaultValue": "\"none\""
            },
            "dragOutside": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "When the \"tabbed\" type leafs are sortable, the items can be dragged\noutside of its tab-list.\n\nThis property lets you specify if this behavior is enabled."
                },
                "attribute": "drag-outside",
                "reflect": false,
                "defaultValue": "false"
            },
            "itemsInfo": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "Map<\n    string,\n    FlexibleLayoutItemExtended<FlexibleLayoutItemModel, FlexibleLayoutLeafType>\n  >",
                    "resolved": "Map<string, FlexibleLayoutItemExtended<FlexibleLayoutItemModel, FlexibleLayoutLeafType>>",
                    "references": {
                        "Map": {
                            "location": "global",
                            "id": "global::Map"
                        },
                        "FlexibleLayoutItemExtended": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/flexible-layout/internal/flexible-layout/types.ts::FlexibleLayoutItemExtended"
                        },
                        "FlexibleLayoutItemModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/flexible-layout/internal/flexible-layout/types.ts::FlexibleLayoutItemModel"
                        },
                        "FlexibleLayoutLeafType": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/flexible-layout/internal/flexible-layout/types.ts::FlexibleLayoutLeafType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the information of each view displayed."
                }
            },
            "layoutSplitterParts": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "Set<string>",
                    "resolved": "Set<string>",
                    "references": {
                        "Set": {
                            "location": "global",
                            "id": "global::Set"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies additional parts to export."
                }
            },
            "model": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "FlexibleLayoutModel",
                    "resolved": "Omit<LayoutSplitterModel, \"items\"> & { items: FlexibleLayoutItemModel[]; }",
                    "references": {
                        "FlexibleLayoutModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/flexible-layout/internal/flexible-layout/types.ts::FlexibleLayoutModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the distribution of the items in the flexible layout."
                }
            },
            "overflow": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "| CssOverflowProperty\n    | `${CssOverflowProperty} ${CssOverflowProperty}`",
                    "resolved": "CssOverflowProperty | \"auto auto\" | \"auto hidden\" | \"auto clip\" | \"auto scroll\" | \"auto visible\" | \"hidden auto\" | \"hidden hidden\" | \"hidden clip\" | \"hidden scroll\" | \"hidden visible\" | \"clip auto\" | \"clip hidden\" | \"clip clip\" | \"clip scroll\" | \"clip visible\" | \"scroll auto\" | \"scroll hidden\" | \"scroll clip\" | \"scroll scroll\" | \"scroll visible\" | \"visible auto\" | \"visible hidden\" | \"visible clip\" | \"visible scroll\" | \"visible visible\"",
                    "references": {
                        "CssOverflowProperty": {
                            "location": "import",
                            "path": "../../../../common/types",
                            "id": "src/common/types.ts::CssOverflowProperty"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Same as the overflow CSS property. This property sets the desired behavior\nwhen content does not fit in the widget's padding box (overflows) in the\nhorizontal and/or vertical direction."
                },
                "attribute": "overflow",
                "reflect": false,
                "defaultValue": "\"visible\""
            },
            "sortable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to enable sorting the tab buttons in the `\"tabbed\"` type leafs by\ndragging them in the tab-list.\n\nIf `false`, the tab buttons can not be dragged out either."
                },
                "attribute": "sortable",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "dragBarDisabled": {}
        };
    }
    static get events() {
        return [{
                "method": "viewItemClose",
                "name": "viewItemClose",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when a item of a view request to be closed."
                },
                "complexType": {
                    "original": "ViewItemCloseInfo",
                    "resolved": "{ itemId: string; itemIndex: number; viewId: string; }",
                    "references": {
                        "ViewItemCloseInfo": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/flexible-layout/internal/flexible-layout/types.ts::ViewItemCloseInfo"
                        }
                    }
                }
            }, {
                "method": "selectedViewItemChange",
                "name": "selectedViewItemChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the selected item change."
                },
                "complexType": {
                    "original": "ViewSelectedItemInfo",
                    "resolved": "{ lastSelectedIndex: number; newSelectedId: string; newSelectedIndex: number; viewId: string; }",
                    "references": {
                        "ViewSelectedItemInfo": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/flexible-layout/internal/flexible-layout/types.ts::ViewSelectedItemInfo"
                        }
                    }
                }
            }, {
                "method": "viewItemReorder",
                "name": "viewItemReorder",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when a widget is dragged and dropped into a view."
                },
                "complexType": {
                    "original": "WidgetReorderInfo",
                    "resolved": "WidgetDragInfo & WidgetDropInfo",
                    "references": {
                        "WidgetReorderInfo": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/flexible-layout/internal/flexible-layout/types.ts::WidgetReorderInfo"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "addSiblingView": {
                "complexType": {
                    "signature": "(parentGroup: string, siblingItem: string, placedInTheSibling: \"before\" | \"after\", viewInfo: FlexibleLayoutLeafModel, takeHalfTheSpaceOfTheSiblingItem: boolean) => Promise<boolean>",
                    "parameters": [{
                            "name": "parentGroup",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "siblingItem",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "placedInTheSibling",
                            "type": "\"after\" | \"before\"",
                            "docs": ""
                        }, {
                            "name": "viewInfo",
                            "type": "LayoutSplitterLeafModel & { accessibleRole?: ViewAccessibleRole; } & FlexibleLayoutLeafConfiguration",
                            "docs": ""
                        }, {
                            "name": "takeHalfTheSpaceOfTheSiblingItem",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "FlexibleLayoutLeafModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/flexible-layout/internal/flexible-layout/types.ts::FlexibleLayoutLeafModel"
                        }
                    },
                    "return": "Promise<boolean>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "refreshLeaf": {
                "complexType": {
                    "signature": "(leafId: string) => Promise<void>",
                    "parameters": [{
                            "name": "leafId",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Schedules a new render for a leaf even if no state changed.",
                    "tags": []
                }
            },
            "removeView": {
                "complexType": {
                    "signature": "(itemId: string) => Promise<FlexibleLayoutViewRemoveResult>",
                    "parameters": [{
                            "name": "itemId",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "FlexibleLayoutViewRemoveResult": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/flexible-layout/internal/flexible-layout/types.ts::FlexibleLayoutViewRemoveResult"
                        }
                    },
                    "return": "Promise<FlexibleLayoutViewRemoveResult>"
                },
                "docs": {
                    "text": "Removes the view that is identified by the given ID.\nThe layout is rearranged depending on the state of the removed view.",
                    "tags": []
                }
            },
            "removeItemPageInView": {
                "complexType": {
                    "signature": "(viewId: string, itemId: string, forceRerender?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "viewId",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "itemId",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "forceRerender",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLChTabRenderElement": {
                            "location": "global",
                            "id": "global::HTMLChTabRenderElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given the view ID and the item id, remove the page of the item from the view.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get listeners() {
        return [{
                "name": "itemClose",
                "method": "handleItemClose",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
